import { useCallback, useMemo } from 'react'
import { nonNullable, uniqueBy } from 'lib/array/arrayUtils'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { isValidCoordinate } from 'tripPlanner/utils/mapMarkerTransforms'
import type { GeoPoint } from 'tripPlanner/types/placeAndDateTime'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { DrawerHotel } from './types'

interface UseHotelListingMapOptions {
  hotels: Array<DrawerHotel>
  savedHotels?: Array<DrawerHotel>
  isLoading: boolean
  isError: boolean
  searchQuery: string
  stopCoordinates?: GeoPoint
  placeName: string
  placeCountry?: string
  stopId?: number
  onClose: () => void
}

function getMappableHotels(hotels: Array<DrawerHotel>) {
  return uniqueBy(
    hotels.filter((hotel): hotel is DrawerHotel & { geoLocation: GeoPoint } => isValidCoordinate(hotel.geoLocation)),
    hotel => hotel.offerId,
  )
}

function useHotelListingMap({
  hotels, savedHotels, isLoading, isError, searchQuery, stopCoordinates, placeName, placeCountry,
  stopId, onClose,
}: UseHotelListingMapOptions) {
  const eventBus = useTripPlannerEventBusOptional()
  const mapHotels = useMemo(() => getMappableHotels([...(savedHotels ?? []), ...hotels]), [hotels, savedHotels])
  const offers = useMemo<Array<ExploreOffer>>(() => mapHotels.map(hotel => ({
    id: hotel.offerId,
    name: hotel.name,
    coordinates: hotel.geoLocation,
    slug: hotel.slug ?? '',
    offerType: hotel.offerType === 'bedbank_hotel' ? 'bedbank_hotel' : 'hotel',
    imageIds: nonNullable(hotel.images.map(image => image.id)),
    location: hotel.location,
    locationDetail: { primary: hotel.location, secondary: hotel.locationSubtitle },
    description: hotel.description,
    rating: hotel.rating,
    price: hotel.price?.total,
    numberOfNights: hotel.price?.nights,
  })), [mapHotels])
  const handleEnlarge = useCallback(() => {
    const centre = offers[0]?.coordinates ?? stopCoordinates
    if (!eventBus || !isValidCoordinate(centre) || isLoading || isError) return
    onClose()
    eventBus.dispatch({
      type: 'show_explore_on_map',
      coordinates: centre,
      categoryKey: 'hotels',
      categoryChosen: true,
      destinationQueryText: searchQuery,
      listingResults: offers,
      returnToListingSearch: {
        vertical: 'hotel',
        placeName,
        placeCountry,
        stopId,
      },
    })
  }, [eventBus, isError, isLoading, offers, onClose, placeCountry, placeName, searchQuery, stopCoordinates, stopId])

  const coordinates = offers[0]?.coordinates ?? stopCoordinates
  const showMap = !isError && isValidCoordinate(coordinates)

  return {
    mapHotels,
    coordinates: showMap ? coordinates : undefined,
    onViewOnMap: eventBus && !isLoading && showMap ? handleEnlarge : undefined,
  }
}

export default useHotelListingMap
